/**
 * Sticky Sections Block - Interactivity API View Script
 *
 * Supports two scroll types:
 * - "stacked": sections stick and overlap with cover effects
 * - "horizontal": container pins, sections scroll horizontally
 *
 * @package BrandyBlocks
 */

import { store, getContext, getElement } from '@wordpress/interactivity';

const HORIZONTAL_MIN_WIDTH = 1024;

/**
 * Stacked mode: cover effects based on vertical scroll coverage.
 */
function initStacked( ref, coverEffect ) {
	if ( coverEffect === 'none' ) {
		return;
	}

	const sections = ref.querySelectorAll( '.brandy-sticky-section' );
	if ( sections.length < 2 ) {
		return;
	}

	const hasScale =
		coverEffect === 'scale-fade' || coverEffect === 'scale';
	const hasFade =
		coverEffect === 'scale-fade' || coverEffect === 'fade';

	let rafId = 0;
	function update() {
		for ( let i = 0; i < sections.length - 1; i++ ) {
			const inner = sections[ i ].querySelector(
				'.brandy-sticky-section__inner'
			);
			if ( ! inner ) continue;

			const nextTop =
				sections[ i + 1 ].getBoundingClientRect().top;
			const sectionHeight = sections[ i ].offsetHeight;
			const coverage = Math.max(
				0,
				Math.min(
					1,
					( sectionHeight - nextTop ) / sectionHeight
				)
			);

			if ( hasScale ) {
				inner.style.setProperty(
					'--sticky-cover-scale',
					String( 1 - coverage * 0.05 )
				);
			}
			if ( hasFade ) {
				inner.style.setProperty(
					'--sticky-cover-opacity',
					String( 1 - coverage * 0.4 )
				);
			}
		}
	}

	function onScroll() {
		if ( ! rafId ) {
			rafId = requestAnimationFrame( () => {
				update();
				rafId = 0;
			} );
		}
	}

	window.addEventListener( 'scroll', onScroll, { passive: true } );
	update();

	return () => {
		window.removeEventListener( 'scroll', onScroll );
		cancelAnimationFrame( rafId );
	};
}

/**
 * Horizontal mode: map vertical scroll to horizontal translateX.
 */
function initHorizontal( ref, coverEffect, childCount ) {
	if ( window.innerWidth < HORIZONTAL_MIN_WIDTH ) {
		return initStacked( ref, coverEffect );
	}

	const track = ref.querySelector( '.brandy-sticky-sections__track' );
	if ( ! track ) {
		return;
	}

	const sections = track.querySelectorAll( '.brandy-sticky-section' );
	const count = childCount || sections.length;
	if ( count < 2 ) {
		return;
	}

	const hasScale =
		coverEffect === 'scale-fade' || coverEffect === 'scale';
	const hasFade =
		coverEffect === 'scale-fade' || coverEffect === 'fade';
	const hasCover = coverEffect !== 'none';

	let rafId = 0;

	function update() {
		if ( window.innerWidth < HORIZONTAL_MIN_WIDTH ) {
			track.style.transform = '';
			return;
		}

		const parentRect = ref.getBoundingClientRect();
		const viewportH = window.innerHeight;
		const scrollRoom = ref.offsetHeight - viewportH;
		if ( scrollRoom <= 0 ) return;

		const progress = Math.max(
			0,
			Math.min( 1, -parentRect.top / scrollRoom )
		);

		const trackWidth = track.offsetWidth;
		const translateX =
			-progress * ( count - 1 ) * trackWidth;
		track.style.transform = `translateX(${ translateX }px)`;

		// Cover effects based on horizontal position
		if ( hasCover ) {
			const sectionProgress = progress * ( count - 1 );
			for ( let i = 0; i < sections.length - 1; i++ ) {
				const inner = sections[ i ].querySelector(
					'.brandy-sticky-section__inner'
				);
				if ( ! inner ) continue;

				const coverage = Math.max(
					0,
					Math.min( 1, sectionProgress - i )
				);

				if ( hasScale ) {
					inner.style.setProperty(
						'--sticky-cover-scale',
						String( 1 - coverage * 0.05 )
					);
				}
				if ( hasFade ) {
					inner.style.setProperty(
						'--sticky-cover-opacity',
						String( 1 - coverage * 0.4 )
					);
				}
			}
		}
	}

	function onScroll() {
		if ( ! rafId ) {
			rafId = requestAnimationFrame( () => {
				update();
				rafId = 0;
			} );
		}
	}

	function onResize() {
		if ( window.innerWidth < HORIZONTAL_MIN_WIDTH ) {
			track.style.transform = '';
		}
	}

	window.addEventListener( 'scroll', onScroll, { passive: true } );
	window.addEventListener( 'resize', onResize, { passive: true } );
	update();

	return () => {
		window.removeEventListener( 'scroll', onScroll );
		window.removeEventListener( 'resize', onResize );
		cancelAnimationFrame( rafId );
	};
}

store( 'brandy/sticky-sections', {
	callbacks: {
		init() {
			const ctx = getContext();
			const { ref } = getElement();
			const coverEffect = ctx.coverEffect ?? 'scale-fade';
			const type = ctx.type ?? 'stacked';

			if ( type === 'horizontal' ) {
				return initHorizontal(
					ref,
					coverEffect,
					ctx.childCount
				);
			}
			return initStacked( ref, coverEffect );
		},
	},
} );
